<template>
	<div v-if="screenshot" v-click-outside="deselectMarkers" data-test="screenshot" class="select-none">
		<div
			v-if="showToolbar"
			class="flex items-center py-6 mb-6 px-16 dark bg-gray-650 border border-gray-550 text-gray-300 rounded w-full"
		>
			<div class="w-1/3 text-gray-100 font-semibold mr-8">Annotate screenshot</div>
			<div class="flex justify-center w-1/3 space-x-4">
				<BaseTooltip text="Arrow">
					<button
						:class="{'text-gray-100': currentMarker === 'arrow'}"
						class="button-icon hover:text-gray-100"
						@click="addArrow"
					>
						<BaseIcon name="mono/arrow" />
					</button>
				</BaseTooltip>
				<BaseTooltip text="Rectangle">
					<button
						:class="{'text-gray-100': currentMarker === 'rectangle'}"
						class="button-icon hover:text-gray-100"
						@click="addRectangle"
					>
						<BaseIcon name="mono/rectangle" />
					</button>
				</BaseTooltip>
				<BaseTooltip text="Pencil">
					<button
						:class="{'text-gray-100': currentMarker === 'freehand'}"
						class="button-icon hover:text-gray-100"
						@click="addFreehand"
					>
						<BaseIcon name="mono/edit" />
					</button>
				</BaseTooltip>
				<BaseTooltip text="Text">
					<button
						:class="{'text-gray-100': currentMarker === 'text'}"
						class="button-icon hover:text-gray-100"
						@click="addText"
					>
						<BaseIcon name="mono/text" />
					</button>
				</BaseTooltip>
			</div>
			<div class="flex justify-end w-1/3 gap-16">
				<button class="link-gray font-semibold" @click="undo">Undo</button>
				<template v-if="!$store.isExtension">
					<button type="button" class="link-gray font-semibold" @click="cancel">Cancel</button>
					<button type="button" class="link-blue font-semibold" @click="save">Save</button>
				</template>
			</div>
		</div>
		<div class="relative rounded overflow-hidden z-0 border border-gray-550">
			<template v-if="!showToolbar">
				<div
					tabindex="0"
					class="absolute h-full w-full inset-0 bg-transparent cursor-default overlay"
					@mouseenter="showOverlayButtons = true"
					@mouseleave="showOverlayButtons = false"
					@focus="showOverlayButtons = true"
				>
					<div
						v-if="showOverlayButtons"
						class="flex m-12 absolute top-0 right-0 dark overlay-buttons divide-x-1 divide-gray-700"
					>
						<BaseTooltip v-if="canUserEditSession" text="Annotate">
							<button class="overlay-button left" @click="onClickAnnotateScreenshot">
								<BaseIcon name="mono/edit" class="w-14 h-14" />
							</button>
						</BaseTooltip>
						<BaseTooltip text="Download">
							<button class="overlay-button" @click="onClickDownload">
								<BaseIcon name="mono/download" class="w-14 h-14" />
							</button>
						</BaseTooltip>
						<BaseTooltip
							v-if="!isMobile"
							:text="$sessionLayout.isMediaPanelMaximized ? 'Minimize' : 'Expand'"
						>
							<button class="overlay-button right" @click="onClickMinimizeExpand">
								<BaseIcon
									v-if="$sessionLayout.isMediaPanelMaximized"
									name="mono/minimize"
									class="w-14 h-14"
								/>
								<BaseIcon v-else name="mono/maximize" class="w-14 h-14" />
							</button>
						</BaseTooltip>
					</div>
				</div>
			</template>
			<div class="relative z-0" @mouseup="autosave">
				<img
					ref="screenshot"
					:src="screenshot"
					alt="screenshot"
					crossorigin="anonymous"
					class="select-none"
					@load="isScreenshotRendered = true"
				/>
			</div>
		</div>
	</div>
</template>

<script>
import * as Sentry from '@sentry/vue'
import {storeToRefs} from 'pinia'
import colors from 'shared/styles/colors.js'
import analytics from 'shared/utils/analytics'
import updateSessionScreenshotMarkers from 'shared/apollo/updateSessionScreenshotMarkers'
import {useWindowSize} from 'shared/composables/useWindowSize'
import {useSessionStore} from 'shared/stores/session'

export default {
	name: 'Screenshot',
	emits: ['minimize', 'expand'],
	setup() {
		const {isMobile} = useWindowSize()
		const sessionStore = useSessionStore()
		const {session, dataLoader, canUserEditSession} = storeToRefs(sessionStore)
		return {
			isMobile,
			session,
			dataLoader,
			canUserEditSession,
		}
	},
	data() {
		return {
			screenshot: null,
			markerjs2: null,
			showOverlayButtons: false,
			isScreenshotRendered: false,
			markerArea: null,
			showToolbar: false,
			currentMarker: null,
		}
	},
	watch: {
		async screenshot(screenshot, previousScreenshot) {
			if (!screenshot) return
			if (previousScreenshot) return
			this.markerjs2 = await import('markerjs2')
			if (this.$store.isExtension) this.startAnnotating()
		},
		// when screenshotMarkers are updated through subscription
		// remove stale markers from sessionStorage and use the new markers for rendering
		'session.screenshotMarkers'() {
			if (sessionStorage) delete sessionStorage.sessionScreenshotMarkers
			this.showMarkerArea()
		},
		isScreenshotRendered(isScreenshotRendered) {
			if (isScreenshotRendered) this.showMarkerArea()
		},
		markerjs2(markerjs2) {
			if (markerjs2) this.showMarkerArea()
		},
	},
	async created() {
		const {screenshot, screenshotEvents} = await this.dataLoader.getScreenshot(this.session)
		if (!screenshot) return
		this.screenshot = screenshot
		document.dispatchEvent(new CustomEvent('sessionScreenshotLoaded', {detail: {screenshot, screenshotEvents}}))
		window.addEventListener('beforeunload', this.autosave)
		window.addEventListener('keyup', this.autosave)
	},
	beforeUnmount() {
		window.removeEventListener('beforeunload', this.autosave)
		window.removeEventListener('keyup', this.autosave)
		this.stopAnnotating()
	},
	methods: {
		showMarkerArea() {
			if (!this.isScreenshotRendered) return
			if (!this.markerjs2) return

			this.markerjs2.Activator.addKey(this.$env.VUE_APP_MARKERJS_KEY)
			this.markerArea = new this.markerjs2.MarkerArea(this.$refs.screenshot)
			this.markerArea.targetRoot = this.$refs.screenshot.parentElement

			// https://markerjs.com/docs/
			// hide native ui
			this.markerArea.uiStyleSettings.hideToolbox = true
			this.markerArea.uiStyleSettings.hideToolbar = true
			this.markerArea.uiStyleSettings.toolbarHeight = 0
			// define markers
			this.markerArea.availableMarkerTypes = [
				this.markerjs2.FrameMarker,
				this.markerjs2.ArrowMarker,
				this.markerjs2.TextMarker,
				this.markerjs2.FreehandMarker,
			]
			this.markerArea.settings.defaultColor = colors['red-500']
			this.markerArea.settings.defaultStrokeColor = colors['red-500']
			this.markerArea.settings.defaultFontFamily =
				"'Inter', 'Segoe UI', Roboto, Oxygen-Sans, Ubuntu, Cantarell, 'Helvetica Neue', sans-serif"
			this.markerArea.settings.defaultFontFamilies = [this.markerArea.settings.defaultFontFamily]
			this.markerArea.settings.newFreehandMarkerOnPointerUp = true
			// rendering options for screenshot download
			this.markerArea.renderAtNaturalSize = true
			this.markerArea.renderImageQuality = 0.9

			// launch marker.js
			this.markerArea.show()
			// restore pre-existing state
			if (
				sessionStorage?.sessionScreenshotMarkers &&
				sessionStorage?.screenshotEditingSessionId === this.session.id
			) {
				const sessionScreenshotMarkers = JSON.parse(sessionStorage.sessionScreenshotMarkers)
				if (sessionScreenshotMarkers?.markers?.length) this.restoreState(sessionScreenshotMarkers)
				this.showToolbar = true
			} else if (this.session.screenshotMarkers?.markers?.length) {
				this.restoreState(this.session.screenshotMarkers)
			}
		},
		restoreState(state) {
			try {
				this.markerArea.restoreState(state)
			} catch (error) {
				// this error should not happen anymore, after this commit:
				// https://github.com/ailon/markerjs2/commit/ee8a25f462af5cc41ec826f59ec59ae0669f9749
				// leaving it here to observe
				error.context = {
					...this.session,
					// remove recorded session imagery to reduce size for sentry
					screenshot: Boolean(this.session?.screenshot),
					video: Boolean(this.session?.video),
				}
				Sentry.captureException(error)
				// eslint-disable-next-line no-console
				console.error('Could not restore screenshot markers state.')
			}
		},
		onClickAnnotateScreenshot() {
			analytics.track('Annotate Screenshot Button Clicked')
			this.startAnnotating()
		},
		onClickDownload() {
			analytics.track('Download Screenshot Button Clicked')

			this.markerArea.render().then(dataUrl => {
				const link = document.createElement('a')
				link.href = dataUrl
				const filename = this.prepareFilenameForDownload(this.session.title)
				link.download = filename
				link.click()
				link.remove()
			})
		},
		prepareFilenameForDownload(sessionTitle) {
			const productName = this.replaceSymbolsWithDashes(this.$store.productName)
			const filename = this.replaceSymbolsWithDashes(sessionTitle)
			return `${filename.substring(0, 50)}_screenshot_${productName}`
		},
		replaceSymbolsWithDashes(inputString) {
			return inputString.replace(/([~!@#$%^&*()_+=`{}[\]|\\:;'<>,./? ])+/g, '-').replace(/^(-)+|(-)+$/g, '')
		},
		onClickMinimizeExpand() {
			analytics.track('Minimize/Expand Screenshot Button Clicked', {
				expand: !this.$sessionLayout.isMediaPanelMaximized,
			})
			this.$sessionLayout.isMediaPanelMaximized ? this.$emit('minimize') : this.$emit('expand')
		},
		startAnnotating() {
			if (sessionStorage) sessionStorage.screenshotEditingSessionId = this.session.id
			this.showToolbar = true
		},
		stopAnnotating() {
			if (sessionStorage) {
				delete sessionStorage.screenshotEditingSessionId
				delete sessionStorage.sessionScreenshotMarkers
			}
			this.showToolbar = false
			this.deselectMarkers()
		},
		addArrow() {
			this.currentMarker = 'arrow'
			this.markerArea.createNewMarker(this.markerjs2.ArrowMarker)
		},
		addRectangle() {
			this.currentMarker = 'rectangle'
			this.markerArea.createNewMarker(this.markerjs2.FrameMarker)
		},
		addFreehand() {
			this.currentMarker = 'freehand'
			this.markerArea.createNewMarker(this.markerjs2.FreehandMarker)
		},
		addText() {
			this.currentMarker = 'text'
			this.markerArea.createNewMarker(this.markerjs2.TextMarker)
		},
		undo() {
			this.markerArea.undo()
		},
		deselectMarkers() {
			this.markerArea?.setCurrentMarker()
			this.persistSessionMarkersInSessionStorage()
		},
		getValidMarkerState(markerState) {
			if (!markerState?.markers?.length) return
			markerState.markers = markerState.markers.filter(marker => {
				return marker.top !== null && marker.left !== null && marker.width !== null && marker.height !== null
			})
			return markerState
		},
		async save() {
			// only happens in web
			if (!this.canUserEditSession) return
			this.stopAnnotating()

			const markerState = this.getValidMarkerState(this.markerArea.getState(true)) // deselectCurrentMarker: true
			await updateSessionScreenshotMarkers({
				sessionId: this.session.id,
				screenshotMarkers: markerState?.markers?.length ? markerState : null,
				userId: this.$store.user.id,
			})
			if (markerState) this.restoreState(markerState)
		},
		cancel() {
			this.stopAnnotating()
			// restore to previous state
			return this.session.screenshotMarkers
				? this.restoreState(this.session.screenshotMarkers)
				: this.markerArea?.clear()
		},
		autosave() {
			// otherwise the button would stay in active mode in the toolbar, even though the tool is no longer selected
			this.currentMarker = null
			this.persistSessionMarkersInSessionStorage()
		},
		persistSessionMarkersInSessionStorage() {
			const markerState = this.getValidMarkerState(this.markerArea?.getState())
			if (!markerState?.markers?.length) return
			if (sessionStorage) sessionStorage.sessionScreenshotMarkers = JSON.stringify(markerState)
		},
	},
}
</script>

<style scoped>
.overlay {
	z-index: 1;
}

.overlay-buttons {
	z-index: 2;
}

.overlay-button {
	width: 32px;
	height: 32px;
	display: flex;
	align-items: center;
	justify-content: center;
	background-color: var(--gray-650);
	color: var(--gray-400);
}

.overlay-button.left {
	width: 36px;
	border-top-left-radius: 16px;
	border-bottom-left-radius: 16px;
}

.overlay-button.right {
	width: 36px;
	border-top-right-radius: 16px;
	border-bottom-right-radius: 16px;
}

.overlay-button:hover {
	background-color: var(--gray-600);
	color: var(--gray-300);
}

.overlay-button:focus {
	outline: none;
}
</style>
